import queryString from 'query-string';
import sentryFetch from '@amzn/sentry-fetch';

const DEFAULT_REQUEST_TIMEOUT_MS = 0;

async function enhancedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    return sentryFetch(url, {
        ...options,
        sentryOptions: {
            followMidwayStepUp: true,
        }
    });
}

function fetchWithRetry(url: string, options: any = {}, maxRetries = 0): Promise<any> {
    const { query = null, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS, ...init } = options;
    init.credentials = 'include';

    const queryStr = query ? `?${typeof query === 'string' ? query : queryString.stringify(query)}` : '';

    async function attempt(retryCount: number): Promise<any> {
        try {
            const response = await fetchWithTimeout(`${url}${queryStr}`, init, timeoutMs);
            if (response.status < 400) {
                return await parseResponse(response);
            }

            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            (error as any).httpStatus = response.status;

            if (retryCount < maxRetries && response.status >= 500) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return attempt(retryCount + 1);
            }
            throw error;
        } catch (error: any) {
            if (retryCount < maxRetries && shouldRetry(error)) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return attempt(retryCount + 1);
            }
            throw error;
        }
    }
    return attempt(0);
}

function shouldRetry(error: any): boolean {
    if (!error) {
        return false;
    }

    const httpStatus = Number(error.httpStatus);
    const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';

    // Respect the caller's timeout budget. Our synthetic client-side timeout means
    // this request already exceeded the allotted wall-clock time, so retrying would
    // only extend that timeout rather than recover from a transient backend failure.
    if ((error as { isClientTimeout?: boolean }).isClientTimeout) {
        return false;
    }

    if (!Number.isNaN(httpStatus)) {
        return httpStatus >= 500;
    }

    return error.name === 'TypeError'
        || error.name === 'TimeoutError'
        || message.includes('failed to fetch')
        || message.includes('network error')
        || message.includes('load failed');
}

async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
    if (!timeoutMs || timeoutMs <= 0) {
        return enhancedFetch(url, options);
    }

    const controller = new AbortController();
    const externalSignal = options.signal;
    let timedOut = false;
    let cleanupExternalAbort: (() => void) | undefined;

    if (externalSignal) {
        if (externalSignal.aborted) {
            controller.abort();
        } else {
            const abortListener = () => controller.abort();
            externalSignal.addEventListener('abort', abortListener, { once: true });
            cleanupExternalAbort = () => externalSignal.removeEventListener('abort', abortListener);
        }
    }

    const timeoutHandle = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);

    try {
        return await enhancedFetch(url, {
            ...options,
            signal: controller.signal,
        });
    } catch (error: any) {
        if (timedOut) {
            const timeoutError = new Error(`Request timed out after ${timeoutMs}ms`);
            timeoutError.name = 'TimeoutError';
            (timeoutError as Error & { isClientTimeout?: boolean }).isClientTimeout = true;
            throw timeoutError;
        }
        throw error;
    } finally {
        clearTimeout(timeoutHandle);
        cleanupExternalAbort?.();
    }
}

async function parseResponse(response: Response): Promise<any> {
    const text = await response.text();
    let parsed: any;
    try {
        parsed = JSON.parse(text);
    } catch {
        return text;
    }
    return normalizeJsonPayload(parsed);
}

function normalizeJsonPayload(value: any): any {
    if (typeof value === 'string') {
        try {
            return normalizeJsonPayload(JSON.parse(value));
        } catch {
            return value;
        }
    }

    if (value && typeof value === 'object' && 'errorMessage' in value && 'errorType' in value) {
        const message = [value.errorType, value.errorMessage].filter(Boolean).join(': ');
        throw new Error(message || 'Backend request failed');
    }

    if (value && typeof value === 'object' && 'statusCode' in value && 'body' in value) {
        const normalizedBody = normalizeJsonPayload((value as { body: unknown }).body);
        const embeddedStatusCode = Number((value as { statusCode?: unknown }).statusCode);
        if (!Number.isNaN(embeddedStatusCode) && embeddedStatusCode >= 400) {
            const message = typeof normalizedBody === 'string'
                ? normalizedBody
                : JSON.stringify(normalizedBody);
            const error = new Error(message || `HTTP ${embeddedStatusCode}`);
            (error as any).httpStatus = embeddedStatusCode;
            throw error;
        }
        return normalizedBody;
    }

    return value;
}

export const getApiUrlForRealm = (stage: string, realm: string): string => {
    // Local dev: point the UI at the local bb server (LocalDevServer on :8080).
    // Gated on NODE_ENV==='development' (same pattern as AuthClient) so unit tests,
    // which run under NODE_ENV='test' in jsdom (hostname 'localhost'), keep the real URL.
    if (process.env.NODE_ENV === 'development'
            && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return 'http://localhost:8080';
    }
    return `https://api-${stage.toLowerCase()}-${realm.toLowerCase()}.sherlock.payments.amazon.dev`;
};

export const makeAwsSignedRequest = (stage: string, realm: string, endpoint: string, options: any = {}): Promise<any> => {
    const baseUrl = getApiUrlForRealm(stage, realm);
    const url = `${baseUrl}/${endpoint.replace(/^\//, '')}`;
    const maxRetries = options.retry !== false ? 2 : 0;

    return fetchWithRetry(url, options, maxRetries);
};
